import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import "./ParallaxDisplay.css";
import document1 from "../../pic/OCA QUALITY CONTROL CERTIFICATE FOR SCIMITAR_EMS.pdf";
import document2 from "../../pic/OCA QUALITY CONTROL CERTIFICATE FOR SCIMITAR_OHS.pdf";
import document3 from "../../pic/OCA QUALITY CONTROL CERTIFICATE FOR SCIMITAR_QMS.pdf";
import document4 from "../../pic/SPEL ISO 31000- 2018 Certifcate.pdf";
import document5 from "../../pic/SPELISO21500-2021ProjectManagment.pdf";

const useIsMobile = (breakpoint = 900) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
};

const pdfData = [
  {
    id: 1,
    title: "Certified ISO 14001:2015 Environmental Management System (EMS).",
    paragraph:
      "This certification reflects our commitment to minimizing our environmental impact, ensuring regulatory compliance, driving sustainable operations and continuously improving our sustainability practices.",
    bgColor: "#ff6b6b", // A unique color for the "PDF" background
    url: document1,
  },
  {
    id: 2,
    title:
      "Certified ISO  45001:2018 Occupational Health and Safety Management Systems (OHSMS).",
    paragraph:
      "This certification reflects our commitment to providing a safe, healthy, and compliant workplace for our employees, contractors, and visitors while continuously improving our safety performance. In Scimitar, we prioritize the well-being of our team through proactive hazard prevention and strict safety protocols.",
    bgColor: "#48dbfb",
    url: document2,
  },
  {
    id: 3,
    title: "Certified ISO 9001:2015 Quality Management Systems (QMS)",
    paragraph:
      "As a pioneer company in heavy oil EOR in Egypt, this certification underscores our commitment to operational excellence, safety, and delivering reliable energy solutions through rigorously controlled processes. In an industry where precision and reliability are non-negotiable, our QMS ensures consistent quality across exploration and production. Our QMS aligns with API, OSHA, and other industry standards to mitigate risks and enhance performance. In Scimitar, we don’t just meet quality standards – we aim to exceed them at every stage.",
    bgColor: "#1dd1a1",
    url: document3,
  },  {
    id: 4,
    title: "Certified BS ISO 31000:2018 Risk Management",
    paragraph:
      "At Scimitar, we have implemented a robust risk management framework based on the international standard ISO 31000:2018. Our systematic approach to identifying, assessing, and treating risk is integral to our strategy and operations, ensuring we protect our assets, reputation, and stakeholders while seizing opportunities for growth.This demonstrates our commitment to excellence, informed decision-making, and sustained business success.",
    bgColor: "#1dd1a1",
    url: document4,
  },
   {
    id: 5,
    title: "Certified ISO 21500:2021 Project, Programme and Portfolio Management — Context and Concepts",
    paragraph:
      "Scimitar is strictly aligned with ISO 21500:2021. Our project management framework delivers safer operations, capital discipline, and reliable project delivery. It provides a structured, governing approach for all upstream project phases—from drilling campaigns to facility upgrades—ensuring disciplined planning, clear communication, and proactive risk control at every stage.",
    bgColor: "#1dd1a1",
    url: document5,
  },
];

const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    body1: { lineHeight: 1.7 },
  },
});

const PDFViewer = () => {
  const [activeId, setActiveId] = useState(pdfData[1].id); // Start with middle card active
  const [expandedId, setExpandedId] = useState(null); // For modal popup
  const isMobile = useIsMobile();

  const activePdfData = pdfData.find((p) => p.id === activeId);

  // --- Desktop Layout Positions ---
  const getDesktopLayoutStyle = (id) => {
    const activeIndex = pdfData.findIndex((p) => p.id === activeId);
    const currentIndex = pdfData.findIndex((p) => p.id === id);

    // Circular logic for previous and next cards
    const prevIndex = (activeIndex - 1 + pdfData.length) % pdfData.length;
    const nextIndex = (activeIndex + 1) % pdfData.length;

    if (currentIndex === activeIndex) {
      return { x: 0, y: 0, rotate: -3, zIndex: 3, scale: 1 };
    }
    if (currentIndex === nextIndex) {
      return { x: 120, y: 60, rotate: 5, zIndex: 2, scale: 0.95 };
    }
    if (currentIndex === prevIndex) {
      return { x: -120, y: 80, rotate: -10, zIndex: 1, scale: 0.9 };
    }
    // Default for any other cards if you add more
    return { x: 0, y: 100, rotate: 0, zIndex: 0, scale: 0.8 };
  };

  const handleCardClick = (id) => {
    if (id === activeId) {
      // If the active card is clicked on desktop, expand it
      setExpandedId(id);
    } else {
      // Otherwise, just set the card as active
      setActiveId(id);
    }
  };

  const handleClose = () => setExpandedId(null);
  const handleScroll = (e) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.firstChild.clientWidth + 16;
    const newIndex = Math.round(scrollLeft / cardWidth);

    setActiveId(pdfData[newIndex]?.id || activeId);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box >
        <Grid
          container
          spacing={0}
        >
          {/* Left Side: PDF Viewer */}
          <Grid
            item
            xs={12}
            md={6}
          >          <div className="parallaxpdf effect ">

            <Box
              className="pdf-viewer-container"
              onScroll={handleScroll}
            >
              {pdfData.map((pdf) => (
                <motion.div
                  key={pdf.id}
                  className="pdf-card"
                  onClick={() => handleCardClick(pdf.id)}
                  animate={isMobile ? {} : getDesktopLayoutStyle(pdf.id)}
                  transition={{ type: "spring", stiffness: 200, damping: 25 }}
                >
                  <iframe
                    src={`${pdf.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=FitH`}
                    title={pdf.title}
                    className="pdf-card-iframe"
                  />
                </motion.div>
              ))}
            </Box></div>
          </Grid>

          {/* Right Side: Title and Paragraph */}
          <Grid
            item
            xs={12}
            md={6}
          >
            <div className="effect parallaxpdfphoto">
            <Box className="text-content">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activePdfData.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Typography
                    variant="h4"
                    color={"#373737"}
                    sx={{
    fontSize: {
      xs: '24px',  // mobile
      sm: '24px',  // small screens
      md: '32px',  // medium and up
    },
  }}
                    gutterBottom
                  >
                    {activePdfData.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="white"
                     sx={{
    fontSize: {
      xs: '12px',  // mobile
      sm: '14px',  // small screens
      md: '18px',  // medium and up
    },
  }}
                    
                  >
                    {activePdfData.paragraph}
                  </Typography>
                </motion.div>
              </AnimatePresence>
            </Box>
            </div>
          </Grid>
        </Grid>

        {/* Expanded PDF Modal (Desktop Only) */}
        <AnimatePresence>
          {expandedId && (
            <>
              <motion.div
                className="backdrop"
                onClick={handleClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
              <motion.div className="expanded-pdf-container">
                <iframe
                  src={`${
                    pdfData.find((p) => p.id === expandedId).url
                  }#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=FitH`}
                  width={"100%"}
                  height={"100%"}
                  title={pdfData.find((p) => p.id === expandedId).title}
                  className="pdf-iframe-expanded"
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </Box>
    </ThemeProvider>
  );
};

export default PDFViewer;
